import { type FC, useState } from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@/shared/hooks";
import { useEmailTemplatePreviewQuery } from "@/partner/entities/marketing";
import { type EditorVariant, variantIsValid } from "../model/editorState";
import { previewPaneState } from "../model/previewPaneState";

const PREVIEW_DEBOUNCE_MS = 600;
const PREVIEW_HEIGHT = 720;
const NO_URL_VARIABLES: readonly string[] = [];

/**
 * Живое превью варианта: HTML рендерит бэкенд (emailTemplatePreview) с
 * примерными данными. Запрос идёт через 600 мс после последней правки и
 * только для валидного варианта. Пока первого HTML нет, в панели скелетон на
 * поверхности темы, а не пустая светлая рамка; при ошибке сообщение и
 * «Повторить». Пока строится превью новой правки, видно прежнее письмо с
 * полосой загрузки. `sandbox=""` запрещает скрипты и навигацию внутри
 * превью: HTML письма приходит с сервера, но содержит пользовательские ссылки.
 */
export const EmailPreviewFrame: FC<{
  variant: EditorVariant;
  /** Переменные-адреса кнопок с сервера (marketingLimits.emailUrlVariables). */
  urlVariables?: readonly string[];
}> = ({ variant, urlVariables = NO_URL_VARIABLES }) => {
  const { t } = useTranslation();
  const debounced = useDebounce(variant, PREVIEW_DEBOUNCE_MS);
  const valid = variantIsValid(debounced, urlVariables);
  const { data, previousData, loading, error, refetch } =
    useEmailTemplatePreviewQuery({
      variables: {
        input: {
          language: debounced.language,
          subject: debounced.subject,
          preheader: debounced.preheader,
          blocks: debounced.blocks,
        },
      },
      skip: !valid,
      fetchPolicy: "no-cache",
      // Без этого явный refetch() не переключает loading (Apollo Client v3
      // считает loading только по смене variables), и «Повторить» выглядел
      // бы нажатым в пустоту: кнопка не пропадала бы, баннер об ошибке не
      // менялся, а повторный клик слал бы ещё один параллельный запрос.
      notifyOnNetworkStatusChange: true,
    });
  const [mobile, setMobile] = useState(false);
  const preview = valid
    ? (data?.emailTemplatePreview ?? previousData?.emailTemplatePreview ?? null)
    : null;
  const html = preview?.html ?? "";
  const pane = previewPaneState({
    valid,
    loading,
    failed: Boolean(error),
    hasHtml: Boolean(html),
  });
  const retry = (
    <Button
      color="inherit"
      size="small"
      disabled={loading}
      onClick={() => {
        // На случай гонки между отрисовкой и обработчиком: пока предыдущий
        // refetch не завершился, второй клик не шлёт ещё один запрос.
        if (!loading) void refetch();
      }}
    >
      {t("emailTemplates.preview.retry")}
    </Button>
  );

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Typography variant="subtitle2" noWrap>
          {preview?.subject || debounced.subject}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mobile ? "mobile" : "desktop"}
          onChange={(_event, value) => value && setMobile(value === "mobile")}
        >
          <ToggleButton
            value="desktop"
            aria-label={t("emailTemplates.preview.desktop")}
          >
            <DesktopWindowsIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="mobile"
            aria-label={t("emailTemplates.preview.mobile")}
          >
            <SmartphoneIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {pane === "invalid" && (
        <Alert severity="info">{t("emailTemplates.preview.fixErrors")}</Alert>
      )}
      {pane === "error" && (
        <Alert severity="error" action={retry}>
          {t("emailTemplates.preview.failed")}
        </Alert>
      )}
      {pane === "ready" && error && !loading && (
        <Alert severity="warning" action={retry}>
          {t("emailTemplates.preview.failed")}
        </Alert>
      )}
      {pane === "loading" && (
        <Skeleton
          variant="rounded"
          height={PREVIEW_HEIGHT}
          aria-label={t("emailTemplates.preview.loading")}
          sx={{ width: mobile ? 375 : "100%", maxWidth: "100%", mx: "auto" }}
        />
      )}
      {pane === "ready" && (
        <Box
          sx={{
            position: "relative",
            mx: "auto",
            width: mobile ? 375 : "100%",
            maxWidth: "100%",
            transition: "width .2s",
          }}
        >
          {loading && (
            <LinearProgress
              sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
            />
          )}
          <Box
            component="iframe"
            title={t("emailTemplates.preview.title")}
            sandbox=""
            srcDoc={html}
            sx={{
              display: "block",
              width: "100%",
              height: PREVIEW_HEIGHT,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              // Светлая «бумага» письма только под готовым HTML: письмо
              // светлое и в тёмной теме кабинета, как в почтовом клиенте.
              bgcolor: "grey.100",
            }}
          />
        </Box>
      )}
    </Stack>
  );
};
